import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DATA_KIND, TraceData, TraceDataRef } from '../../api/trace';

export interface ITraceDataViewProps {
    reference: TraceDataRef;
    /** Fetches the body. Kept as a prop so this component owns no environment. */
    read: (reference: TraceDataRef) => Promise<TraceData>;
}

/** A moment, the way the rest of the panel writes one. */
const moment = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
          });
};

/** Characters, as a size somebody can weigh. */
const sizeLabel = (length: number): string =>
    length < 1024
        ? `${length} tekens`
        : `${(length / 1024).toLocaleString('nl-NL', {
              maximumFractionDigits: 1,
          })} kB`;

/**
 * What was actually in an object at one point on its road.
 *
 * The question the rest of the panel leads to and none of it answers. A station says the
 * copy is still there and a timeline row says the send handler broke on it; neither says
 * what was in it, which is where nearly every real failure lives - a customer Exact does not
 * have yet, a VAT code it refuses, a line without a product.
 *
 * Fetched when it is opened rather than with the trace. These bodies are tens of kilobytes
 * each and one trace can point at a dozen of them.
 */
export const TraceDataView = (props: ITraceDataViewProps) => {
    const { reference } = props;

    const [data, setData] = useState<TraceData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    useEffect(() => {
        let alive = true;

        setLoading(true);
        setError(null);

        props
            .read(reference)
            .then((answer) => {
                if (alive) {
                    setData(answer);
                }
            })
            .catch((ex: unknown) => {
                if (alive) {
                    setError(
                        ex instanceof Error
                            ? ex.message
                            : 'De opgeslagen gegevens konden niet opgehaald worden.'
                    );
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });

        return () => {
            alive = false;
        };
        // Refetches when the pointer changes, which is when a different body is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        reference.kind,
        reference.objectType,
        reference.objectGuid,
        reference.eventType,
        reference.eventId,
    ]);

    const copy = () => {
        if (!data?.content) {
            return;
        }

        void navigator.clipboard
            .writeText(data.content)
            .then(() => setCopied(true))
            .catch(() => undefined);
    };

    return (
        <div className="trace-data">
            <div className="trace-data__head">
                <span className="trace-data__kind" title={DATA_KIND[reference.kind].hint}>
                    {DATA_KIND[reference.kind].label}
                </span>

                {data?.table && (
                    <span
                        className="trace-data__where"
                        title="De tabel en de sleutel waar dit uit komt, zodat je het met de hand terug kunt vinden."
                    >
                        {data.table} · {data.partitionKey} / {data.rowKey}
                    </span>
                )}

                {data?.at && (
                    <span
                        className="trace-data__at"
                        title="Wanneer deze rij voor het laatst is geschreven."
                    >
                        {moment(data.at)}
                    </span>
                )}

                {!!data?.length && (
                    <span className="trace-data__size">
                        {sizeLabel(data.length)}
                    </span>
                )}

                {data?.content && (
                    <button
                        type="button"
                        className="requeue-link ms-auto"
                        onClick={copy}
                    >
                        {copied ? 'Gekopieerd' : 'Kopiëren'}
                    </button>
                )}
            </div>

            {loading && (
                <p className="trace-data__state">
                    <FontAwesomeIcon icon="spinner" spin className="me-2" />
                    Ophalen uit de tabel
                </p>
            )}

            {!loading && (error || data?.error) && (
                <p className="trace-data__error">
                    <FontAwesomeIcon
                        icon="triangle-exclamation"
                        className="me-2"
                    />
                    {error ?? data?.error}
                </p>
            )}

            {/*
                Truncation is said above the body rather than at its end. A reader who
                scrolls to the bottom of a cut-off invoice and finds it ends mid-line would
                conclude the object itself is broken.
            */}
            {data?.truncated && (
                <p className="trace-data__state">
                    Alleen het begin: dit is afgekapt op{' '}
                    {sizeLabel(data.content?.length ?? 0)} van{' '}
                    {sizeLabel(data.length)}.
                </p>
            )}

            {data?.content && (
                <pre className="trace-data__body">{data.content}</pre>
            )}
        </div>
    );
};
